"use client";

import { useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { BookOpen, Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { BlurFade } from "@/components/ui/blur-fade";
import { DotPattern } from "@/components/ui/dot-pattern";
import { useTranslation } from "@/lib/i18n/context";

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    const email = new FormData(e.currentTarget).get("email") as string;
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setSent(true);
      } else {
        toast.error(t.auth.forgot.genericError);
      }
    } catch {
      toast.error(t.auth.forgot.genericError);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative">
      <DotPattern className="absolute inset-0 text-primary/10 z-0" width={24} height={24} cr={1} />
      <BlurFade delay={0.1} className="relative z-10">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
              {sent ? (
                <MailCheck className="h-6 w-6 text-primary-foreground" />
              ) : (
                <BookOpen className="h-6 w-6 text-primary-foreground" />
              )}
            </div>
            <CardTitle className="text-2xl font-display font-bold tracking-tight">
              {sent ? t.auth.forgot.sentTitle : t.auth.forgot.title}
            </CardTitle>
            <CardDescription>{sent ? t.auth.forgot.sentMessage : t.auth.forgot.subtitle}</CardDescription>
          </CardHeader>

          {sent ? (
            <CardFooter className="flex flex-col gap-4">
              <Link href="/login" className="w-full">
                <Button variant="outline" className="w-full">
                  {t.auth.forgot.backToLogin}
                </Button>
              </Link>
            </CardFooter>
          ) : (
            <form onSubmit={onSubmit}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t.auth.forgot.email}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder={t.auth.forgot.emailPlaceholder}
                    required
                    disabled={loading}
                    autoFocus
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-4">
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {loading ? t.auth.forgot.sending : t.auth.forgot.submit}
                </Button>
                <Link
                  href="/login"
                  className="text-sm text-muted-foreground underline-offset-4 hover:underline"
                >
                  {t.auth.forgot.backToLogin}
                </Link>
              </CardFooter>
            </form>
          )}
        </Card>
      </BlurFade>
    </div>
  );
}
